'use client';

import React, { memo, useMemo } from 'react';

interface AppLogoProps {
  src?: string;
  iconName?: string;
  size?: number;
  className?: string;
  onClick?: () => void;
}

const AppLogo = memo(function AppLogo({
  size = 44,
  className = '',
  onClick,
}: AppLogoProps) {
  const containerClassName = useMemo(() => {
    const classes = ['flex items-center gap-2.5'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  const iconSize = size;

  return (
    <div className={containerClassName} onClick={onClick}>
      {/* SVG Icon Mark */}
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 44 44"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        {/* Background circle */}
        <circle cx="22" cy="22" r="22" fill="url(#logoGradient)" />

        {/* Sparkle / star accent top-right */}
        <circle cx="33" cy="10" r="2.5" fill="white" opacity="0.7" />

        {/* House / building silhouette */}
        <path
          d="M22 10L10 20H13V33H19V26H25V33H31V20H34L22 10Z"
          fill="white"
          opacity="0.95"
        />

        {/* Cleaning brush stroke at bottom */}
        <rect x="13" y="34" width="18" height="2.5" rx="1.25" fill="white" opacity="0.6" />

        {/* Small leaf / hospitality accent */}
        <path
          d="M26 15 C28 13 31 14 30 17 C29 20 26 19 26 15Z"
          fill="white"
          opacity="0.5"
        />

        <defs>
          <linearGradient id="logoGradient" x1="0" y1="0" x2="44" y2="44" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#0EA5E9" />
          </linearGradient>
        </defs>
      </svg>

      {/* Business Name */}
      <div className="flex flex-col leading-tight">
        <span
          className="font-bold text-foreground tracking-tight"
          style={{ fontSize: Math.max(14, iconSize * 0.36), lineHeight: 1.15 }}
        >
          Tota
        </span>
        <span
          className="text-muted-foreground font-medium tracking-wide uppercase"
          style={{ fontSize: Math.max(8, iconSize * 0.2), lineHeight: 1.2, letterSpacing: '0.04em' }}
        >
          Hospitality &amp; Cleaning
        </span>
      </div>
    </div>
  );
});

export default AppLogo;
